/**
 * 🎓 Phase 15.1 — Floating Help Button (bottom-right "?")
 *
 * Always-on-top button reachable on mobile thumb. Tapping it opens
 * the Help Center modal. Hidden on:
 *   - Anonymous (no session token) — onboarding is for authed artists
 *   - During an active tutorial overlay — would visually conflict
 *   - On /admin routes — admins don't need it
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTutorial } from "@/components/tutorial/TutorialProvider";
import HelpCenterModal from "@/components/tutorial/HelpCenterModal";

const FloatingHelpButton = () => {
  const [open, setOpen] = useState(false);
  const { activeSection } = useTutorial();
  const location = useLocation();

  // Allow any part of the app (e.g. mobile drawer "Tutorials" link or
  // a future profile-dropdown entry) to open the Help Center.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("rgm:open-help-center", onOpen);
    return () => window.removeEventListener("rgm:open-help-center", onOpen);
  }, []);

  const hidden =
    activeSection
    || !(typeof window !== "undefined" && localStorage.getItem("session_token"))
    || location.pathname.startsWith("/admin")
    // Starter Studio owns its own bottom transport + mixer at
    // iPhone width. The floating ? cannot coexist with those
    // controls without covering them, so hide the affordance
    // entirely while the artist is inside Studio. Help is still
    // reachable via the header menu.
    || location.pathname === "/studio"
    || location.pathname.startsWith("/studio/")
    // Phase 1B iPhone QA — the Create Reel flow has its own bottom
    // transport (Start/Stop/Flip/Retake/Continue + Post Reel). The
    // floating ? sits exactly over the Continue / Post button at
    // iPhone width, so suppress it for the whole Reel creator route.
    // Help is still reachable from the header menu elsewhere.
    || location.pathname === "/create/reel"
    || location.pathname.startsWith("/create/reel")
    || location.pathname === "/reels/create"
    || location.pathname.startsWith("/reels/create")
    // Phase 1D predeploy — same reasoning for the vertical Reels
    // viewer: the right-edge action rail (Like / Comment / Gift /
    // Share) and the Create FAB occupy the exact pixels the floating
    // ? would cover at iPhone width, AND the viewer's modal overlays
    // (comments sheet, gift sheet, owner Edit/Delete) all mount
    // inside this route tree so a single ``startsWith("/reels")``
    // suppression keeps help off every Reel surface.
    || location.pathname === "/reels"
    || location.pathname.startsWith("/reels")
    // Contest Judging workspace — the fixed bottom transport
    // (← Prev / Save draft / Submit review / Next →) sits in the
    // exact pixels the floating ? would occupy at iPhone width.
    // Suppress on the Judge Inbox + per-contest Judge Workspace
    // AND the Admin per-contest Judging page so the Next → control
    // stays tappable while a judge scores entries.
    || location.pathname === "/judge"
    || location.pathname.startsWith("/judge/")
    || /^\/admin\/contests\/[^/]+\/judging(\/|$)/.test(location.pathname);

  if (hidden) {
    return open
      ? <HelpCenterModal open={open} onOpenChange={setOpen} />
      : null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="floating-help-button"
        aria-label="Open tutorial center"
        className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-500 text-white text-xl font-black shadow-2xl shadow-amber-500/40 hover:scale-110 active:scale-95 transition-all flex items-center justify-center border-2 border-white/10"
      >
        ?
      </button>
      <HelpCenterModal open={open} onOpenChange={setOpen} />
    </>
  );
};

export default FloatingHelpButton;
